import {
  DMAutomation,
  DMAutomationPlatform,
  DMAutomationTrigger,
} from "@markit/common.types";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import SelectAutomationTriggerModal from "./SelectAutomationTriggerModal";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { useCallback, useState } from "react";
import { DMAutomationConfirmDeleteModal } from "../Items/DMAutomationConfirmDeleteModal";
import DMAutomationTriggerPreview from "../Items/DMAutomationTriggerPreview";
import { getNumDMAutomationTriggers } from "../../../../utils/dmAutomationUtils";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../../../redux/slices/accountSlice";
import AlertContainer from "../../../Containers/AlertContainer";
import ConfirmActionModal from "../../../Containers/ConfirmPopups/ConfirmActionModal";
import RectangleButton from "../../../Buttons/RectangleButton";
import { isAccountConnected } from "../../../../utils/socialAccountUtils";

type DMAutomationTriggersSectionProps = {
  dmAutomation: DMAutomation;
  dmAutomationTriggers: DMAutomationTrigger[];
  isEditing: boolean;
  onEditTrigger: (trigger: DMAutomationTrigger) => void;
  showAll?: boolean;
  onViewAllTriggers?: () => void;
};

const DMAutomationTriggersSection = (
  props: DMAutomationTriggersSectionProps
) => {
  const {
    dmAutomation,
    dmAutomationTriggers,
    isEditing,
    onEditTrigger,
    showAll = false,
    onViewAllTriggers,
  } = props;
  const { accountData, instagramAccounts, facebookPages } =
    useSelector(getAccountSlice);
  const { uid } = accountData;
  const [selectTriggerModal, setSelectTriggerModal] = useState(false);
  const [deleteTriggerModal, setDeleteTriggerModal] = useState<
    DMAutomationTrigger | undefined
  >();
  const [cloneTriggerModal, setCloneTriggerModal] = useState<
    DMAutomationTrigger | undefined
  >();
  const [showAlert, setShowAlert] = useState("");

  const styles = {
    sectionContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.WHITE1,
      borderRadius: 12,
    },
    showcaseBody: {
      paddingInline: 14,
      gap: 20,
    },
  };

  const displayedTriggers = showAll
    ? dmAutomationTriggers
    : dmAutomationTriggers.slice(0, 3);
  const extraCount = dmAutomationTriggers.length - 3;
  const hasMoreTriggers = !showAll && extraCount > 0;

  // The header action button (Create New / View All) only shows while editing.
  // Not rendered in the "show all" panel — that panel supplies its own header.
  const showHeaderButton = isEditing;

  const addTriggerOnPress = useCallback(async () => {
    const numTriggers = await getNumDMAutomationTriggers(uid, dmAutomation.id);
    if (numTriggers > 50) {
      setShowAlert(
        "You have the maximum number of triggers for this automation."
      );
      return;
    }
    setSelectTriggerModal(true);
  }, [uid, dmAutomation.id]);

  const deleteTriggerOnPress = useCallback(
    async (trigger: DMAutomationTrigger) => {
      const numTriggers = await getNumDMAutomationTriggers(
        uid,
        dmAutomation.id
      );
      if (numTriggers === 1) {
        setShowAlert("You must have at least one trigger in an automation.");
        return;
      }
      setDeleteTriggerModal(trigger);
    },
    [uid, dmAutomation.id]
  );

  const confirmCloneTrigger = useCallback(() => {
    if (!cloneTriggerModal) {
      return;
    }
    const clonedTrigger: DMAutomationTrigger = {
      ...cloneTriggerModal,
      id: "",
      createdAt: new Date().toISOString(),
      numTriggered: 0,
    };
    setCloneTriggerModal(undefined);
    onEditTrigger(clonedTrigger);
  }, [cloneTriggerModal, onEditTrigger]);

  // A trigger whose account has since been disconnected can only be deleted
  const isTriggerAccountConnected = useCallback(
    (trigger: DMAutomationTrigger) =>
      dmAutomation.platform === DMAutomationPlatform.FACEBOOK
        ? facebookPages.some(
            (page) =>
              page.id === trigger.igAccountId && isAccountConnected(page.status)
          )
        : instagramAccounts.some(
            (account) =>
              account.id === trigger.igAccountId &&
              isAccountConnected(account.status)
          ),
    [dmAutomation.platform, instagramAccounts, facebookPages]
  );

  const getTriggerDropdownItems = useCallback(
    (trigger: DMAutomationTrigger): DropdownMenuItem[] => [
      ...(isTriggerAccountConnected(trigger)
        ? [
            {
              title: "Clone",
              icon: "ion:copy",
              key: "clone",
              onPress: () => setCloneTriggerModal(trigger),
            },
          ]
        : []),
      {
        title: "Delete",
        icon: "ion:trash",
        key: "delete",
        onPress: () => deleteTriggerOnPress(trigger),
        isDestructive: true,
      },
    ],
    [deleteTriggerOnPress, isTriggerAccountConnected]
  );

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        {!showAll ? (
          <div className="AlignedRowSpaced" style={{ gap: 14 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon
                icon="ion:flash"
                height={18}
                style={{ color: Colors.BLACK }}
              />
              <span className="sectionTitle">Triggers</span>
            </div>
            {showHeaderButton ? (
              hasMoreTriggers && onViewAllTriggers ? (
                <RectangleButton
                  buttonLabel="View All"
                  iconRight={
                    <Icon
                      icon="ion:chevron-forward"
                      height={14}
                      style={{ color: Colors.BLACK }}
                    />
                  }
                  onPress={onViewAllTriggers}
                  altColor={Colors.WHITE}
                  altTextColor={Colors.BLACK}
                  altBorderRadius={100}
                  containerStyles={{
                    padding: "8px 14px",
                    width: "fit-content",
                    border: `1px solid ${Colors.GRAY2}`,
                  }}
                />
              ) : (
                <RectangleButton
                  buttonLabel="Create New"
                  iconRight={
                    <Icon
                      icon="ion:add-circle"
                      height={18}
                      style={{ color: Colors.BLACK }}
                    />
                  }
                  onPress={addTriggerOnPress}
                  altColor={Colors.WHITE}
                  altTextColor={Colors.BLACK}
                  altBorderRadius={100}
                  containerStyles={{
                    padding: "8px 14px",
                    width: "fit-content",
                    border: `1px solid ${Colors.GRAY2}`,
                  }}
                />
              )
            ) : null}
          </div>
        ) : null}
        <StandardBorderedContainer
          containerStyles={{
            ...styles.sectionContainer,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="ColumnNormal" style={styles.showcaseBody}>
            {displayedTriggers.map((trigger) => (
              <div
                key={trigger.id}
                className="AlignedRowSpaced"
                style={{ gap: 14 }}
              >
                <DMAutomationTriggerPreview
                  trigger={trigger}
                  platform={dmAutomation.platform}
                  iconSize={50}
                />
                {isEditing ? (
                  <div className="AlignedRow" style={{ gap: 10 }}>
                    <DropdownMenu
                      dropdownItems={getTriggerDropdownItems(trigger)}
                      trigger={["click"]}
                      placement="bottomRight"
                      altWidth={140}
                    >
                      <Icon
                        icon="mdi:dots-horizontal"
                        height={20}
                        style={{ color: Colors.BLACK, cursor: "pointer" }}
                      />
                    </DropdownMenu>
                    {isTriggerAccountConnected(trigger) ? (
                      <div>
                        <Icon
                          className={"EditButton EditButtonLight"}
                          icon={"ion:create"}
                          height={20}
                          onClick={() => onEditTrigger(trigger)}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
            {hasMoreTriggers ? (
              <div className="ColumnCentering">
                <span className="bodySubtext">and {extraCount} more...</span>
              </div>
            ) : null}
          </div>
        </StandardBorderedContainer>
      </div>
      {selectTriggerModal ? (
        <SelectAutomationTriggerModal
          showModal={selectTriggerModal}
          setShowModal={setSelectTriggerModal}
          existingDmAutomation={dmAutomation}
          onSelectTrigger={(trigger) => onEditTrigger(trigger)}
        />
      ) : null}
      {deleteTriggerModal ? (
        <DMAutomationConfirmDeleteModal
          id={deleteTriggerModal.id}
          dmAutomationId={dmAutomation.id}
          type="Trigger"
          showDeleteModal={!!deleteTriggerModal}
          setShowDeleteModal={() => setDeleteTriggerModal(undefined)}
        />
      ) : null}
      {showAlert ? (
        <AlertContainer
          headerComp={showAlert}
          subHeaderComp={undefined}
          closeModal={() => setShowAlert("")}
          hideModal={showAlert === ""}
        />
      ) : null}
      {cloneTriggerModal ? (
        <ConfirmActionModal
          heading="Clone this trigger?"
          subtext="This will duplicate the trigger with the same settings, keywords, and public replies. You can edit the copy before creating it."
          confirmButtonText="Clone"
          hideModal={!cloneTriggerModal}
          setIsVisible={() => setCloneTriggerModal(undefined)}
          confirmOnPress={confirmCloneTrigger}
        />
      ) : null}
    </>
  );
};

export default DMAutomationTriggersSection;
